import React, { useState, useEffect } from 'react';
import { useReportStaticTexts } from '../../hooks/useReportStaticTexts';
import { FaTimes, FaTrash, FaPlus } from 'react-icons/fa';

const ReportStaticTextsModal = () => {
  const { 
    isModalOpen, 
    closeModal, 
    createText, 
    updateText, 
    selectedText,
    modalMode
  } = useReportStaticTexts();

  const initialFormState = {
    module_name: '',
    report_name: '',
    text_values: []
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or selected text changes
  useEffect(() => {
    if (isModalOpen) {
      if (modalMode === 'edit' && selectedText) {
        // Handle both string and array formats for text_values
        let textValues = [];
        if (typeof selectedText.text_values === 'string') {
          try {
            textValues = JSON.parse(selectedText.text_values);
          } catch (e) {
            textValues = [];
          }
        } else if (Array.isArray(selectedText.text_values)) {
          textValues = selectedText.text_values;
        }
        
        setFormData({
          module_name: selectedText.module_name || '',
          report_name: selectedText.report_name || '',
          text_values: textValues
        });
      } else {
        setFormData(initialFormState);
      }
      setErrors({});
    }
  }, [isModalOpen, selectedText, modalMode]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.module_name) newErrors.module_name = 'Module name is required';
    if (!formData.report_name) newErrors.report_name = 'Report name is required';
    if (!formData.text_values || formData.text_values.length === 0) {
      newErrors.text_values = 'At least one text value is required';
    }
    
    // Validate each text value
    formData.text_values.forEach((val, idx) => {
      if (!val.label) newErrors[`label_${idx}`] = 'Label is required';
      if (!val.text) newErrors[`text_${idx}`] = 'Text is required';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if any
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleTextValueChange = (index, field, value) => {
    const newTextValues = [...formData.text_values];
    newTextValues[index] = {
      ...newTextValues[index],
      [field]: value
    };
    
    setFormData(prev => ({
      ...prev,
      text_values: newTextValues
    }));
    
    // Clear error for this field
    const errorKey = `${field}_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: undefined
      }));
    }
  };

  const handleAddTextValue = () => {
    setFormData(prev => ({
      ...prev,
      text_values: [...prev.text_values, { label: '', text: '' }]
    }));
  };

  const handleRemoveTextValue = (index) => {
    setFormData(prev => ({
      ...prev,
      text_values: prev.text_values.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Send text_values as array, not as JSON string
      const submitData = {
        ...formData,
        text_values: formData.text_values
      };

      if (modalMode === 'create') {
        await createText(submitData);
      } else {
        const textId = selectedText._id || selectedText.id;
        await updateText(textId, submitData);
      }
      closeModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">
            {modalMode === 'create' ? 'Add New Report Static Text' : 'Edit Report Static Text'}
          </h2>
          <button 
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className="overflow-y-auto p-4 max-h-[calc(90vh-120px)]">
          <form onSubmit={handleSubmit}>
            {/* Module Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Module Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="module_name"
                value={formData.module_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.module_name ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                placeholder="e.g., blood, strength, balance, dexaReport, vo2max"
              />
              {errors.module_name && <p className="text-red-500 text-xs mt-1">{errors.module_name}</p>}
            </div>

            {/* Report Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="report_name"
                value={formData.report_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.report_name ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                placeholder="e.g., biological_age, grip_strength, balance_score"
              />
              {errors.report_name && <p className="text-red-500 text-xs mt-1">{errors.report_name}</p>}
            </div>

            {/* Text Values */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Text Values <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={handleAddTextValue}
                  className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors"
                >
                  <FaPlus size={12} /> Add Value
                </button>
              </div>

              {errors.text_values && <p className="text-red-500 text-xs mb-2">{errors.text_values}</p>}

              <div className="space-y-3">
                {formData.text_values.map((textValue, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-sm font-semibold text-gray-700">Value {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => handleRemoveTextValue(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>

                    {/* Label */}
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Label <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={textValue.label}
                        onChange={(e) => handleTextValueChange(index, 'label', e.target.value)}
                        className={`w-full px-3 py-2 border ${errors[`label_${index}`] ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm`}
                        placeholder="e.g., low, normal, high"
                      />
                      {errors[`label_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`label_${index}`]}</p>}
                    </div>

                    {/* Text */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Text <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={textValue.text}
                        onChange={(e) => handleTextValueChange(index, 'text', e.target.value)}
                        className={`w-full px-3 py-2 border ${errors[`text_${index}`] ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm`}
                        placeholder="Enter the text content..."
                        rows="3"
                      />
                      {errors[`text_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`text_${index}`]}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportStaticTextsModal;
